import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Users, Ticket, TrendingUp, BarChart3, Map as MapIcon,
  Building2, AlertCircle, RefreshCw, ExternalLink, Flame, Snowflake, Clock, Zap,
  Sparkles, DollarSign, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import VenueMapViewer from '../venue/VenueMapViewer';
import SalesVelocityChart from './SalesVelocityChart';

const HEAT_CONFIG = {
  urgent:   { label: 'Urgente',    icon: Zap,       color: 'red',     bg: 'bg-red-500/10 border-red-500/40',         text: 'text-red-400',     dot: 'bg-red-500',     pulse: true  },
  hot:      { label: 'Caliente',   icon: Flame,     color: 'orange',  bg: 'bg-orange-500/10 border-orange-500/40',   text: 'text-orange-400',  dot: 'bg-orange-500',  pulse: false },
  warm:     { label: 'Tibio',      icon: TrendingUp,color: 'amber',   bg: 'bg-amber-500/10 border-amber-500/40',     text: 'text-amber-400',   dot: 'bg-amber-500',   pulse: false },
  cool:     { label: 'Fresco',     icon: Clock,     color: 'sky',     bg: 'bg-sky-500/10 border-sky-500/30',         text: 'text-sky-400',     dot: 'bg-sky-500',     pulse: false },
  cold:     { label: 'Sin ventas', icon: Snowflake, color: 'zinc',    bg: 'bg-zinc-500/10 border-zinc-500/30',       text: 'text-zinc-400',    dot: 'bg-zinc-500',    pulse: false },
  soldout:  { label: 'Agotado',    icon: AlertCircle,color:'fuchsia', bg: 'bg-fuchsia-500/10 border-fuchsia-500/40', text: 'text-fuchsia-400', dot: 'bg-fuchsia-500', pulse: false }
};

const formatEta = (hours) => {
  if (hours == null) return null;
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${Math.round(hours)} h`;
  if (hours < 24 * 7) return `${Math.round(hours / 24)} días`;
  if (hours < 24 * 30) return `${Math.round(hours / 24 / 7)} semanas`;
  return `${Math.round(hours / 24 / 30)} meses`;
};

/**
 * Live venue statistics tab. Shows real-time occupancy of a venue linked to an event,
 * with per-section breakdown (capacity vs sold) and a small list of useful info.
 *
 * Refreshes every 30s to track ticket sales as they happen.
 */
const VenueStatsTab = ({ eventId, event, apiMode = 'producer' }) => {
  const [venue, setVenue] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Endpoint variants per role. Admin uses neutral /events/* routes that bypass
  // the producer subscription guard but compute the same analytics.
  const velocityUrl = apiMode === 'admin'
    ? `/events/${eventId}/sales-velocity`
    : `/producers/me/events/${eventId}/sales-velocity`;
  const historyUrl = apiMode === 'admin'
    ? `/events/${eventId}/price-history`
    : `/producers/me/events/${eventId}/price-history`;
  const isReadOnly = apiMode === 'admin';

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const historyPromise = api.get(historyUrl).catch(() => ({ data: null }));
        if (!event?.venue_id) {
          const [velRes, histRes] = await Promise.all([
            api.get(velocityUrl).catch(() => ({ data: null })),
            historyPromise
          ]);
          if (!mounted) return;
          setVelocity(velRes.data);
          setPriceHistory(histRes.data);
          setLoading(false);
          return;
        }
        const [venueRes, tiersRes, velRes, histRes] = await Promise.all([
          api.get(`/venues/public/${event.venue_id}`),
          api.get(`/events/${eventId}/tiers/public`),
          api.get(velocityUrl).catch(() => ({ data: null })),
          historyPromise
        ]);
        if (!mounted) return;
        setVenue(venueRes.data?.venue || null);
        setTiers(tiersRes.data || []);
        setVelocity(velRes.data);
        setPriceHistory(histRes.data);
      } catch (e) {
        console.error('Error loading venue stats:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const interval = setInterval(() => {
      if (mounted) {
        setRefreshing(true);
        load().finally(() => mounted && setRefreshing(false));
      }
    }, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [eventId, event?.venue_id, apiMode]);
  
  // Build velocity map (tier_id -> velocity info)
  const velocityByTier = useMemo(() => {
    const map = {};
    (velocity?.tiers || []).forEach(t => { map[t.tier_id] = t; });
    return map;
  }, [velocity]);

  // Collect actionable pricing suggestions across all hot/urgent tiers.
  const pricingSuggestions = useMemo(() => 
    (velocity?.tiers || [])
      .filter(t => t.pricing_suggestion && t.available > 0)
      .map(t => ({ tier_id: t.tier_id, tier_name: t.tier_name, ...t.pricing_suggestion })),
    [velocity]
  );

  const [applyingTierId, setApplyingTierId] = useState(null);

  const handleApplyPriceSuggestion = async (suggestion) => {
    if (!suggestion?.tier_id) return;
    setApplyingTierId(suggestion.tier_id);
    try {
      await api.patch(`/producers/me/events/${eventId}/tiers/${suggestion.tier_id}/price`, {
        price: suggestion.suggested_price,
        source: `suggestion_${suggestion.heat_level}`
      });
      toast.success(`Precio actualizado a $${suggestion.suggested_price.toLocaleString()}`);
      // Refresh velocity + tiers + history
      const [tiersRes, velRes, histRes] = await Promise.all([
        api.get(`/events/${eventId}/tiers/public`),
        api.get(`/producers/me/events/${eventId}/sales-velocity`).catch(() => ({ data: null })),
        api.get(`/producers/me/events/${eventId}/price-history`).catch(() => ({ data: null }))
      ]);
      setTiers(tiersRes.data || []);
      setVelocity(velRes.data);
      setPriceHistory(histRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar el precio');
    } finally {
      setApplyingTierId(null);
    }
  };
  
  // Build section stats by matching tiers to sections
  const sectionStats = useMemo(() => {
    if (!venue) return [];
    const rawSections = venue.sections || venue.elements || [];
    return rawSections.map(section => {
      const matchingTier = tiers.find(t => 
        section.tier_id === t.id ||
        section.linkedTierId === t.id ||
        section.name?.toLowerCase() === t.name?.toLowerCase()
      );
      const capacity = section.capacity || 0;
      const sold = matchingTier?.sold || 0;
      const total = matchingTier?.quantity || capacity;
      const percent = total > 0 ? Math.min(100, Math.round((sold / total) * 100)) : 0;
      const velInfo = matchingTier ? velocityByTier[matchingTier.id] : null;
      return {
        id: section.id,
        name: section.name || 'Sin nombre',
        color: section.color || '#4F46E5',
        section_type: section.section_type,
        capacity,
        total,
        sold,
        available: Math.max(0, total - sold),
        percent,
        price: matchingTier?.price ?? section.price,
        tier_linked: !!matchingTier,
        heat_level: velInfo?.heat_level || (total > 0 && sold >= total ? 'soldout' : 'cold'),
        velocity_24h: velInfo?.tickets_per_hour_24h ?? 0,
        sellout_eta_hours: velInfo?.sellout_eta_hours ?? null
      };
    });
  }, [venue, tiers, velocityByTier]);
  
  const totals = useMemo(() => {
    return sectionStats.reduce((acc, s) => {
      acc.capacity += s.capacity;
      acc.total += s.total;
      acc.sold += s.sold;
      acc.available += s.available;
      return acc;
    }, { capacity: 0, total: 0, sold: 0, available: 0 });
  }, [sectionStats]);
  
  const overallPercent = totals.total > 0 ? Math.round((totals.sold / totals.total) * 100) : 0;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-violet-500" size={32} />
      </div>
    );
  }
  
  if (!event?.venue_id || !venue) {
    return (
      <div className="text-center py-20 text-zinc-500">
        <Building2 size={56} className="mx-auto mb-4 text-zinc-700" />
        <p className="font-bold text-white mb-2">Este evento no tiene venue vinculado</p>
        <p className="text-sm mb-6">Vinculá un plano para ver estadísticas por sección en tiempo real.</p>
        <Link to={`/producer/events/${eventId}/manage`}>
          <Button variant="outline" className="border-zinc-700">
            <MapPin size={14} className="mr-2" />
            Vincular Venue
          </Button>
        </Link>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="venue-stats-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={18} className="text-violet-500" />
            <h3 className="text-xl font-bold text-white" data-testid="venue-stats-name">{venue.name}</h3>
            {refreshing && <RefreshCw size={14} className="animate-spin text-zinc-500" />}
          </div>
          {(venue.address || venue.city) && (
            <p className="text-sm text-zinc-500 flex items-center gap-1.5">
              <MapPin size={12} />
              {[venue.address, venue.city, venue.province].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
        <Link to={`/producer/venues`}>
          <Button variant="ghost" size="sm" className="text-zinc-400">
            <ExternalLink size={14} className="mr-2" />
            Editar venue
          </Button>
        </Link>
      </div>
      
      {/* Overall stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard 
          icon={Users} 
          label="Capacidad total" 
          value={totals.total.toLocaleString()}
          color="violet"
          testId="venue-stat-capacity"
        />
        <StatCard 
          icon={Ticket} 
          label="Vendidas en vivo" 
          value={totals.sold.toLocaleString()}
          color="emerald"
          testId="venue-stat-sold"
        />
        <StatCard 
          icon={MapIcon} 
          label="Disponibles" 
          value={totals.available.toLocaleString()}
          color="amber"
          testId="venue-stat-available"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Ocupación" 
          value={`${overallPercent}%`}
          color={overallPercent >= 80 ? 'red' : overallPercent >= 50 ? 'amber' : 'emerald'}
          testId="venue-stat-percent"
        />
      </div>
      
      {/* Visual map */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <MapIcon size={16} className="text-violet-500" />
          <span className="text-sm font-semibold text-white">Plano visual</span>
          <span className="ml-auto text-xs text-zinc-500">Los colores muestran disponibilidad en tiempo real</span>
        </div>
        <div className="p-4">
          <VenueMapViewer venue={venue} tiers={tiers} showLegend />
        </div>
      </div>
      
      {/* Sales velocity sparkline + price change markers */}
      {velocity?.time_series?.points?.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="velocity-chart-card">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-500" />
            <span className="text-sm font-semibold text-white">Ventas por hora (últimas {velocity.time_series.window_hours}h)</span>
            <span className="ml-auto text-xs text-zinc-500">Líneas verticales = cambios de precio</span>
          </div>
          <SalesVelocityChart 
            points={velocity.time_series.points}
            markers={velocity.time_series.price_change_markers || []}
            windowHours={velocity.time_series.window_hours}
          />
        </div>
      )}
      
      {/* Dynamic pricing suggestions banner */}
      {pricingSuggestions.length > 0 && (
        <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-red-500/10 border border-amber-500/40 rounded-xl overflow-hidden" data-testid="pricing-suggestions">
          <div className="px-4 py-3 border-b border-amber-500/20 flex items-center gap-2">
            <Sparkles size={16} className="text-amber-400" />
            <span className="text-sm font-bold text-white">Sugerencias inteligentes</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
              {pricingSuggestions.length} oportunidad{pricingSuggestions.length !== 1 ? 'es' : ''}
            </span>
          </div>
          <div className="divide-y divide-amber-500/10">
            {pricingSuggestions.map(s => {
              const heatCfg = HEAT_CONFIG[s.heat_level] || HEAT_CONFIG.hot;
              const HeatIcon = heatCfg.icon;
              const isUrgent = s.heat_level === 'urgent';
              return (
                <div key={s.tier_id} className="p-4" data-testid={`pricing-suggestion-${s.tier_id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${heatCfg.bg}`}>
                      <HeatIcon size={18} className={heatCfg.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-white">{s.tier_name}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${heatCfg.text} ${heatCfg.bg} border border-current/30`}>
                          {heatCfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-300">{s.reason}</p>
                      <p className="text-xs text-zinc-500 mt-1">{s.action_label}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="p-2 bg-black/30 rounded">
                      <p className="text-[10px] text-zinc-500 uppercase">Precio actual</p>
                      <p className="text-sm font-mono font-bold text-zinc-300">${s.current_price.toLocaleString()}</p>
                    </div>
                    <div className={`p-2 rounded border ${isUrgent ? 'bg-red-500/10 border-red-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                      <p className="text-[10px] text-zinc-500 uppercase">Sugerido (+{s.increase_percent}%)</p>
                      <p className={`text-sm font-mono font-bold ${isUrgent ? 'text-red-400' : 'text-emerald-400'}`} data-testid={`suggested-price-${s.tier_id}`}>
                        ${s.suggested_price.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                      <p className="text-[10px] text-zinc-500 uppercase">Uplift potencial</p>
                      <p className="text-sm font-mono font-bold text-amber-400">+${s.potential_uplift.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    {isReadOnly ? (
                      <div className="text-xs px-3 py-2 bg-zinc-800/60 border border-zinc-700 rounded text-zinc-400">
                        Vista admin · solo lectura. Para aplicar, la productora debe usar su panel.
                      </div>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApplyPriceSuggestion(s)}
                          disabled={applyingTierId === s.tier_id}
                          className={`${isUrgent ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
                          data-testid={`apply-price-${s.tier_id}`}
                        >
                          {applyingTierId === s.tier_id ? (
                            <RefreshCw size={14} className="mr-2 animate-spin" />
                          ) : (
                            <Check size={14} className="mr-2" />
                          )}
                          Aplicar ${s.suggested_price.toLocaleString()}
                        </Button>
                        <Link to={`/producer/events/${eventId}/manage?tab=tickets`}>
                          <Button size="sm" variant="ghost" className="text-zinc-400">
                            <DollarSign size={14} className="mr-1" />
                            Editar tanda manualmente
                          </Button>
                        </Link>
                      </>
                    )}
                    <span className="ml-auto text-[10px] text-zinc-500">
                      Sobre {s.available} entradas disponibles
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-4 py-2 bg-black/20 border-t border-amber-500/10">
            <p className="text-[10px] text-zinc-500">
              Las sugerencias se actualizan según la velocidad de venta en las últimas 24h. Aplicar el precio actualiza la tanda inmediatamente.
            </p>
          </div>
        </div>
      )}
      
      {/* Heatmap of demand */}
      {sectionStats.some(s => s.tier_linked) && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="venue-heatmap">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
            <Flame size={16} className="text-orange-500" />
            <span className="text-sm font-semibold text-white">Mapa térmico de demanda</span>
            <span className="ml-auto text-xs text-zinc-500">
              Velocidad de venta en las últimas 24h y predicción de agotamiento
            </span>
          </div>
          
          {/* Overall velocity bar */}
          {velocity?.overall?.total_sold > 0 && (
            <div className="px-4 py-3 bg-zinc-900/40 border-b border-zinc-800 flex items-center gap-4 flex-wrap text-xs">
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">Velocidad global (24h):</span>
                <span className="text-white font-bold" data-testid="velocity-overall">
                  {velocity.overall.tickets_per_hour_24h} tickets/h
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">Total vendidos:</span>
                <span className="text-emerald-400 font-bold">{velocity.overall.total_sold}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">Últimas 24h:</span>
                <span className="text-amber-400 font-bold">+{velocity.overall.recent_sold_24h}</span>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {sectionStats.filter(s => s.tier_linked).map(s => {
              const heatCfg = HEAT_CONFIG[s.heat_level] || HEAT_CONFIG.cold;
              const HeatIcon = heatCfg.icon;
              return (
                <div 
                  key={s.id} 
                  className={`relative rounded-xl border p-3 ${heatCfg.bg} transition-transform hover:scale-[1.02]`}
                  data-testid={`heatmap-section-${s.id}`}
                >
                  {/* Pulse animation for urgent */}
                  {heatCfg.pulse && (
                    <div className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"/>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"/>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="font-semibold text-white truncate text-sm">{s.name}</span>
                    </div>
                    <span className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${heatCfg.text} ${heatCfg.bg} border ${heatCfg.bg.replace('/10', '/40')}`}>
                      <HeatIcon size={10} />
                      {heatCfg.label}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-zinc-500 mb-0.5">Velocidad 24h</p>
                      <p className={`font-bold ${heatCfg.text}`}>
                        {s.velocity_24h > 0 ? `${s.velocity_24h} t/h` : 'Sin actividad'}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-500 mb-0.5">Se agota en</p>
                      <p className="font-bold text-white">
                        {s.heat_level === 'soldout' ? '—' : (formatEta(s.sellout_eta_hours) || '∞')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-[10px] text-zinc-500">
                    <span>{s.sold} / {s.total}</span>
                    <span>{s.percent}% ocupado</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="px-4 py-3 border-t border-zinc-800 flex flex-wrap gap-3 text-[10px]">
            {Object.entries(HEAT_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={key} className={`flex items-center gap-1.5 ${cfg.text}`}>
                  <Icon size={11} />
                  <span>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Per-section breakdown */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <BarChart3 size={16} className="text-violet-500" />
          <span className="text-sm font-semibold text-white">Desglose por sección</span>
        </div>
        <div className="divide-y divide-zinc-800">
          {sectionStats.length === 0 ? (
            <div className="px-4 py-12 text-center text-zinc-500 text-sm">
              No hay secciones configuradas en este venue.
            </div>
          ) : (
            sectionStats.map(s => (
              <div key={s.id} className="px-4 py-3" data-testid={`venue-section-${s.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="font-medium text-white truncate">{s.name}</span>
                    {!s.tier_linked && (
                      <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                        Sin tanda vinculada
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono text-white">
                      <span className="text-emerald-400">{s.sold}</span>
                      <span className="text-zinc-500"> / {s.total}</span>
                    </p>
                    {s.price > 0 && (
                      <p className="text-[10px] text-zinc-500">${s.price.toLocaleString()}</p>
                    )}
                  </div>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${s.percent}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className={`h-full rounded-full ${
                      s.percent >= 90 ? 'bg-red-500' :
                      s.percent >= 70 ? 'bg-amber-500' :
                      s.percent >= 30 ? 'bg-violet-500' :
                      'bg-emerald-500'
                    }`}
                  />
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {s.available} disponibles · {s.percent}% ocupado
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Helpful info */}
      {sectionStats.some(s => !s.tier_linked) && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 flex gap-3">
          <AlertCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-400 mb-1">Algunas secciones no tienen tanda vinculada</p>
            <p className="text-amber-400/70 text-xs">
              Para vender entradas asignadas a una sección, vinculá una tanda (ticket tier) desde el editor del venue o el tab de Entradas.
            </p>
          </div>
        </div>
      )}
      
      {/* Pricing history audit log */}
      {priceHistory && priceHistory.total_changes > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="pricing-history">
          <button
            onClick={() => setShowHistory(v => !v)}
            className="w-full px-4 py-3 border-b border-zinc-800 flex items-center gap-2 hover:bg-zinc-900/80 transition-colors"
            data-testid="toggle-pricing-history"
          >
            <Clock size={16} className="text-violet-500" />
            <span className="text-sm font-semibold text-white">Historial de cambios de precio</span>
            <span className="ml-2 text-[10px] uppercase tracking-wider text-violet-400 bg-violet-500/10 border border-violet-500/30 rounded-full px-2 py-0.5">
              {priceHistory.total_changes} cambio{priceHistory.total_changes !== 1 ? 's' : ''}
            </span>
            <span className="ml-auto text-xs text-zinc-500">{showHistory ? 'Ocultar' : 'Ver detalles'}</span>
          </button>
          {showHistory && (
            <div className="divide-y divide-zinc-800">
              {priceHistory.entries.map(entry => {
                const isIncrease = entry.change_amount > 0;
                const sourceLabel = {
                  manual: { label: 'Manual', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30' },
                  suggestion_hot: { label: '🔥 Sugerencia Hot', color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
                  suggestion_urgent: { label: '⚡ Sugerencia Urgent', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
                  api: { label: 'API', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30' },
                }[entry.source] || { label: entry.source, color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30' };
                const ts = entry.created_at ? new Date(entry.created_at) : null;
                return (
                  <div key={entry.id} className="px-4 py-3" data-testid={`history-entry-${entry.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium text-white text-sm">{entry.tier_name}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${sourceLabel.color}`}>
                            {sourceLabel.label}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500">
                          {ts ? ts.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono">
                          <span className="text-zinc-500 line-through">${entry.old_price.toLocaleString()}</span>
                          <span className="text-zinc-600 mx-1">→</span>
                          <span className={isIncrease ? 'text-emerald-400 font-bold' : 'text-red-400 font-bold'}>
                            ${entry.new_price.toLocaleString()}
                          </span>
                        </p>
                        {entry.change_percent != null && (
                          <p className={`text-[10px] ${isIncrease ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isIncrease ? '+' : ''}{entry.change_percent}%
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-zinc-800/50 grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <span className="text-zinc-600">Vendidas al momento del cambio:</span>{' '}
                        <span className="text-zinc-300 font-mono">{entry.sold_at_change}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-zinc-600">Ventas desde el cambio:</span>{' '}
                        <span className={`font-mono font-bold ${entry.sales_since_change > 0 ? 'text-emerald-400' : 'text-zinc-500'}`} data-testid={`history-sales-since-${entry.id}`}>
                          {entry.sales_since_change > 0 ? `+${entry.sales_since_change}` : '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="px-4 py-2 bg-black/20 border-t border-zinc-800 text-[10px] text-zinc-500">
            Usá el historial para medir si los aumentos de precio aceleran o frenan las ventas.
          </div>
        </div>
      )}
      
      <p className="text-xs text-zinc-600 text-center">
        Las estadísticas se actualizan automáticamente cada 30 segundos.
      </p>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => {
  const colors = {
    violet: 'border-violet-500/30 bg-violet-500/5 text-violet-400',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-400',
    red: 'border-red-500/30 bg-red-500/5 text-red-400'
  };
  return (
    <div className={`border rounded-xl p-3 ${colors[color] || colors.violet}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl font-black text-white" data-testid={testId}>{value}</p>
    </div>
  );
};

export default VenueStatsTab;
